import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Header actions component that manages cart notifications.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #onCartUpdate = (event) => {
    const cartCount = event.detail.resource?.item_count;

    if (cartCount === undefined) {
      // An add to cart carries no cart object (fetching one would put another
      // round trip in front of the shopper). The cart icon is the authority on
      // the visible count and updates from this same event, so announce what
      // it settles on -- a microtask later, since it may handle the event
      // after this does.
      queueMicrotask(() => {
        const counted = this.querySelector('cart-icon')?.currentCartCount;
        if (typeof counted !== 'number' || !Number.isFinite(counted)) return;

        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${counted}`;
      });
      return;
    }

    this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
